import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  BlockStack,
  Box,
  InlineGrid,
  TextField,
  Select,
  Text,
  Button,
  Divider,
  Badge,
  Spinner,
  Banner,
} from "@shopify/polaris";
import { DeleteIcon, PlusIcon } from "@shopify/polaris-icons";
import supabase from "../../supabase-client";
import { UserAuth } from "../../context/AuthContext";
import { formatPrice } from "../../utility/funcs";
import HoverArmButton from "../CustomComponents/HoverArmButton/HoverArmButton";
import AlegraProductPicker from "./AlegraProductPicker";
import {
  getReservationUnallocated,
  allocateAvailableToInvoice,
  alegraPaymentForm,
} from "../../utility/payments";
import {
  CFDI_USE_OPTIONS,
  REGIMEN_OPTIONS,
  PAYMENT_FORM_OPTIONS,
  PAYMENT_TYPE_OPTIONS,
  CHARGE_TYPE_ALEGRA,
  CHARGE_TYPE_LABEL,
  alegraRequest,
  findOrCreateContact,
  saveBillingSettings,
  buildAlegraItems,
  SUBLEASE_RENT_ALEGRA_ITEM_ID,
  applyRoundingAdjustment,
  buildInvoicePayload,
  buildRentLines,
  applyInvoiceDiscount,
  getReservationDiscountSummary,
  buildSubleaseVehicleLabel,
  prefillBillingFromEntity,
  publicoGeneralBilling,
  getInvoicesForReservation,
  getInvoicedConceptsForReservation,
  getBilledChargeIds,
  markChargesBilled,
  billedPeriodsFromInvoices,
  reservationLastBillableDay,
  addDaysISO,
  computeUninvoicedGaps,
  detachInvoicePayments,
  getAlegraCancelState,
  cfdiUuidOf,
} from "../../utility/invoicing";

// Inclusive day count for a billing period. Both `from` and `to` are INCLUSIVE
// (periodTo is the last billable day), matching buildRentLines' convention, so
// 2026-07-04 → 2026-07-09 is 6 days — the same quantity buildRentLines emits.
const inclusiveDays = (from, to) => {
  if (!from || !to) return null;
  const ms = new Date(to) - new Date(from);
  if (!isFinite(ms)) return null;
  return Math.max(1, Math.round(ms / 86400000) + 1);
};

function InvoiceModal({ open, onClose, reservation, customer, company, charges, discounts, onSuccess, substitutesInvoice = null }) {
  const { session } = UserAuth();
  // When set, this modal issues a CORRECTED invoice that substitutes a prior
  // CFDI: it bills the original's period by default, and on stamp it cancels the
  // original (motivo 01 + relatedFolio) and links/transfers payments.
  const isSubstitution = !!substitutesInvoice;

  const [billing, setBilling] = useState({
    rfc: "",
    razonSocial: "",
    regimenFiscal: "",
    codigoPostal: "",
    cfdiUse: "G03",
    paymentForm: "transfer",
    paymentType: "PUE",
    creditDays: "",
  });
  const [items, setItems] = useState([]);
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  // Manual discount the user applies to THIS invoice, plus the reservation's
  // available (recorded − already-invoiced) discount for guidance.
  const [discountApply, setDiscountApply] = useState({ days: "", amount: "" });
  const [discountSummary, setDiscountSummary] = useState(null);

  // When period changes, recalculate quantity on rental items (those with alegraItemId)
  const handlePeriodFromChange = (val) => {
    setPeriodFrom(val);
    checkPeriodOverlap(val, periodTo);
    if (val && periodTo) {
      const days = inclusiveDays(val, periodTo);
      setItems(prev => prev.map(item =>
        // Manually re-scoping the period overrides the agreed-amount target, so
        // drop intendedTotal and fall back to the line's literal value.
        item.isRental ? { ...item, quantity: days, intendedTotal: undefined } : item
      ));
    }
  };

  const handlePeriodToChange = (val) => {
    setPeriodTo(val);
    checkPeriodOverlap(periodFrom, val);
    if (periodFrom && val) {
      const days = inclusiveDays(periodFrom, val);
      setItems(prev => prev.map(item =>
        // Manually re-scoping the period overrides the agreed-amount target, so
        // drop intendedTotal and fall back to the line's literal value.
        item.isRental ? { ...item, quantity: days, intendedTotal: undefined } : item
      ));
    }
  };
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // True while the modal is auto-calculating the default period + line items on
  // open. The period/concepts UI is hidden behind a spinner until it settles so
  // the user can't edit blank/half-built fields.
  const [loadingItems, setLoadingItems] = useState(false);
  const [invoicedPeriods, setInvoicedPeriods] = useState([]);
  // Collapsed by default: only the most recent invoiced period is shown, the
  // rest expand on click.
  const [showAllPeriods, setShowAllPeriods] = useState(false);
  // Timbrar stays disabled until the user scrolls to the bottom of the modal
  // (forces a full review of the invoice before stamping). Detected via an
  // IntersectionObserver on a sentinel at the end of the content.
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const bottomSentinelRef = useRef(null);
  const [periodError, setPeriodError] = useState(null);
  const [totalInvoiced, setTotalInvoiced] = useState(0);
  const [invoiceNotes, setInvoiceNotes] = useState("");
  const [emailTo, setEmailTo] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState(null);
  // Pay-first → PUE automation: available unallocated payment on the reservation
  // and whether the user has manually overridden the auto PUE/PPD decision.
  const [availablePayment, setAvailablePayment] = useState(0);
  const [coverForm, setCoverForm] = useState(null);
  const [paymentTypeTouched, setPaymentTypeTouched] = useState(false);

  // Check for period overlap. period_from and period_to are both INCLUSIVE
  // (period_to is "billed through end of that day"), so use <= / >= — strict
  // inequalities miss the case where two periods share a boundary day.
  const checkPeriodOverlap = (from, to) => {
    if (!from || !to) { setPeriodError(null); return; }
    const newFrom = new Date(from);
    const newTo = new Date(to);
    for (const p of invoicedPeriods) {
      const pFrom = new Date(p.period_from);
      const pTo = new Date(p.period_to);
      if (newFrom <= pTo && newTo >= pFrom) {
        setPeriodError(`Traslape con factura ${p.invoice_number || "#" + p.id} (${p.period_from} al ${addDaysISO(p.period_to || p.period_from, 1)})`);
        return;
      }
    }
    setPeriodError(null);
  };

  // Load billing defaults from customer/company
  useEffect(() => {
    if (!open) return;
    setError(null);
    setSuccess(null);
    setPeriodError(null);
    setLoadingItems(true);
    setInvoicedPeriods([]);
    setShowAllPeriods(false);
    setScrolledToBottom(false);
    setTotalInvoiced(0);
    setEmailSent(false);
    setEmailError(null);
    setEmailSending(false);
    setPaymentTypeTouched(false);
    setAvailablePayment(0);
    setCoverForm(null);
    if (reservation?.id) {
      getReservationUnallocated(reservation.id).then(({ total: avail, transactions }) => {
        setAvailablePayment(avail);
        // Card-aware: a debit charge is forma 28, not 04.
        setCoverForm(alegraPaymentForm(transactions[0]) || null);
      });
    }

    const entity = company || customer;
    const isCompany = !!company;

    if (entity) {
      const { billing: pf, emailTo: eto } = prefillBillingFromEntity(entity, isCompany);
      setBilling(pf);
      setEmailTo(eto);
    }

    // Build line items from reservation — fetch list price and Alegra product name
    if (reservation) {
      const isSublease = !!reservation.is_sublease;
      const plate = isSublease
        ? reservation.sublease_plate || ""
        : reservation.car?.plate || "";
      // Sublease vehicle data for the line description (subleases carry no
      // Alegra item to hold it). Owned cars get this from their Alegra item.
      const subleaseBrand = isSublease
        ? reservation.sublease_model?.brand?.name || ""
        : "";
      const subleaseModel = isSublease
        ? reservation.sublease_model?.show_as || reservation.sublease_model?.name || ""
        : "";
      const hoursExtra = reservation.totals?.hoursExtra || 0;
      const alegraItemId = isSublease
        ? null
        : reservation.car?.alegra_item_id || null;

      // Load previously invoiced periods for this reservation. Includes both
      // directly-linked invoices AND multi-reservation invoices referencing
      // this reservation via invoice_reservations.
      const loadInvoicedPeriods = async () => {
        const fetched = await getInvoicesForReservation(reservation.id);
        // When substituting, the original is being replaced — exclude it so it
        // doesn't count as "already invoiced" or block its own period.
        const allInvoices = isSubstitution
          ? fetched.filter((i) => i.id !== substitutesInvoice.id)
          : fetched;
        const periods = billedPeriodsFromInvoices(allInvoices);
        setInvoicedPeriods(periods);
        // For invoices split across reservations, only count this reservation's
        // portion (the getInvoicesForReservation helper sets `reservation_portion`).
        setTotalInvoiced(
          allInvoices.reduce(
            (sum, i) => sum + (i.reservation_portion ?? i.total ?? 0),
            0
          )
        );

        // Per-concept dedup for fees / hours: a fee is proposed only if its
        // Alegra product id hasn't already been billed. (Rent is deduped
        // separately by period below.) Substitution intentionally re-bills the
        // original invoice, and product ids can't tell the original's lines
        // from another invoice's, so it keeps the old "first invoice only"
        // rule there.
        let conceptBilled = (itemId) => periods.length > 0;
        if (!isSubstitution) {
          try {
            const concepts = await getInvoicedConceptsForReservation(reservation.id);
            conceptBilled = (itemId) => concepts.isConceptBilled(itemId);
          } catch {
            /* keep the conservative default on read failure */
          }
        }

        // Extra charges dedup by their own row instead: several charges share
        // one product per type, so a product-id rule hides every charge after
        // the first of its type. This lookup is DB-only (no Alegra), so it
        // stands on its own — and for a substitution the invoice being replaced
        // is excluded, putting its charges back on the new CFDI.
        let chargeBilled = () => false;
        try {
          const billedIds = await getBilledChargeIds(
            reservation.id,
            isSubstitution ? substitutesInvoice.id : null
          );
          chargeBilled = (chargeId) => billedIds.has(Number(chargeId));
        } catch {
          /* a charge we can't resolve is proposed, and can be unticked */
        }

        // Default period start: the reservation start, or the day after the
        // last already-invoiced period.
        let defaultFrom = "";
        // Reservation's last billable day — used only to detect when every rent
        // day is already invoiced (a fee/charge-only top-up), not as the default
        // end. periodTo is INCLUSIVE in buildRentLines, so this is
        // first_day + (JS-rounded days − 1), not the raw upper(date_range)::date.
        let resLastBillable = "";

        if (reservation.date_range) {
          const clean = reservation.date_range.replace(/[\[\]()]/g, "");
          const parts = clean.split(",");
          defaultFrom = parts[0]?.replace(/"/g, "").trim().substring(0, 10) || "";
          resLastBillable = reservationLastBillableDay(reservation.date_range);
        }

        if (periods.length > 0) {
          // Start at the FIRST un-invoiced day, which is not necessarily the day
          // after the latest invoice: periods can be billed out of order, and
          // jumping to max(period_to)+1 hid any earlier gap (see
          // computeUninvoicedGaps). No gaps → all rent days already invoiced, so
          // this is a fee/charge-only top-up. Leave the period blank so no rent
          // line is built (the rent block is gated on defaultFrom && defaultTo)
          // and the overlap check stays quiet.
          if (!resLastBillable) {
            // No usable date_range to walk — keep the old "day after the latest
            // invoice" behaviour rather than blanking the period.
            const sorted = [...periods].sort(
              (a, b) => new Date(b.period_to) - new Date(a.period_to)
            );
            defaultFrom = addDaysISO(sorted[0].period_to, 1);
          } else {
            const gaps = computeUninvoicedGaps(defaultFrom, resLastBillable, periods);
            defaultFrom = gaps[0]?.from || "";
          }
        }

        // Default to a SINGLE day (from == to) on purpose: the vendor must
        // consciously pick the real end date instead of accepting a full
        // remaining-span default, which invited billing the wrong window.
        let defaultTo = defaultFrom;

        // Substitution re-bills the ORIGINAL invoice's period (it's being
        // replaced), so default to that span rather than advancing past it.
        if (isSubstitution) {
          if (substitutesInvoice.period_from) defaultFrom = substitutesInvoice.period_from;
          if (substitutesInvoice.period_to) defaultTo = substitutesInvoice.period_to;
        }

        setPeriodFrom(defaultFrom);
        setPeriodTo(defaultTo);

        const newItems = [];

        // Resolve the Alegra product details ONCE per modal open. The same
        // base description + alegraItemId is reused across every rent line
        // emitted by buildRentLines (one per overlapping rate section).
        let alegraProductName = isSublease
          ? "Renta de vehículo"
          : `Renta de vehículo ${plate}`;
        let resolvedAlegraItemId = alegraItemId;
        let resolvedAlegraUnit = null;
        if (alegraItemId) {
          try {
            const alegraItem = await alegraRequest(`items/${alegraItemId}`, "GET");
            if (alegraItem?.name) alegraProductName = alegraItem.name;
            if (alegraItem?.inventory?.unit) resolvedAlegraUnit = alegraItem.inventory.unit;
            else if (alegraItem?.unit) resolvedAlegraUnit = alegraItem.unit;
          } catch {}
        } else if (isSublease) {
          // Every sublease bills against ONE fixed generic product (vehicle data
          // goes in the line note). Never delete/create products here: that left
          // ~190 duplicate "Renta de vehículo" products in Alegra.
          resolvedAlegraItemId = SUBLEASE_RENT_ALEGRA_ITEM_ID;
          resolvedAlegraUnit = "DAY";
        }

        // Rent lines: one per rate section overlapping [defaultFrom, defaultTo].
        // Each line is billed at the section's own daily_price; sections inserted
        // for backfilled reservations carry a frozen billable_amount so historical
        // totals stay intact.
        if (defaultFrom && defaultTo) {
          const rent = await buildRentLines(reservation.id, defaultFrom, defaultTo, {
            plate, alegraItemId: resolvedAlegraItemId, isSublease,
            subleaseBrand, subleaseModel,
          });
          for (const line of rent.lines) {
            newItems.push({
              description: `${alegraProductName} - Periodo ${line.sectionPeriodFrom} al ${addDaysISO(line.sectionPeriodTo, 1)}`,
              quantity: line.quantity,
              unitPrice: line.unitPrice,
              // Honor the per-unit discount buildRentLines distributes for
              // reservations whose total_price carries a booking discount.
              discount: line.discount || 0,
              // Exact intended gross (before the 2-dp per-unit discount
              // rounding) so the displayed/stamped total hits the agreed price.
              intendedTotal: line.intendedTotal,
              alegraItemId: resolvedAlegraItemId,
              preResolvedUnit: resolvedAlegraUnit,
              // Owned cars without a linked product resolve it at stamping
              // (ensureCarAlegraItem) instead of creating one per invoice.
              reservationId: reservation.id,
              isSublease,
              linePrefix: isSublease
                ? buildSubleaseVehicleLabel({ plate, brand: subleaseBrand, model: subleaseModel })
                : null,
              isRental: true,
            });
          }
        }

        if (hoursExtra > 0 && !conceptBilled(271)) {
          newItems.push({
            description: "Horas extra",
            quantity: 1,
            unitPrice: hoursExtra,
            discount: 0,
            alegraItemId: 271,
          });
        }

        if (reservation.delivery?.fee > 0 && !conceptBilled(272)) {
          newItems.push({
            description: "Entrega a domicilio",
            quantity: 1,
            unitPrice: reservation.delivery.fee,
            discount: 0,
            alegraItemId: 272,
          });
        }

        if (reservation.return?.fee > 0 && !conceptBilled(270)) {
          newItems.push({
            description: "Recolección a domicilio",
            quantity: 1,
            unitPrice: reservation.return.fee,
            discount: 0,
            alegraItemId: 270,
          });
        }

        // Add extra charges (damages, cleaning, etc.) — only those that no live
        // invoice has billed yet.
        if (charges?.length > 0) {
          charges.forEach((charge) => {
            const alegraItemId = CHARGE_TYPE_ALEGRA[charge.charge_type] || 279;
            if (chargeBilled(charge.id)) return;
            const label =
              CHARGE_TYPE_LABEL[charge.charge_type] ||
              charge.description ||
              "Cargo extra";
            newItems.push({
              description: label,
              chargeId: charge.id,
              quantity: 1,
              unitPrice: charge.amount,
              discount: 0,
              alegraItemId,
              // Specific detail (e.g. "Gasolina faltante ~25 L: 16/16 → 8/16")
              // goes into the per-line note below the concept name.
              linePrefix:
                charge.description && charge.description !== label
                  ? charge.description
                  : null,
            });
          });
        }

        // Stable per-line key so the manual discount can map effective values
        // back to each line (InvoiceModal items are otherwise index-only).
        setItems(newItems.map((it, i) => ({ ...it, key: it.key || `item-${i}` })));
      };

      loadInvoicedPeriods().finally(() => setLoadingItems(false));
      // Available reservation discount (recorded − already invoiced) for the
      // control. Default: pre-fill the pending discount so it's applied to this
      // invoice by default (finish it off); the user can clear it.
      setDiscountApply({ days: "", amount: "" });
      if (reservation?.id) {
        getReservationDiscountSummary(reservation.id)
          .then((s) => {
            setDiscountSummary(s);
            setDiscountApply({
              days: s?.remainingDays > 0 ? String(s.remainingDays) : "",
              amount: s?.remainingMoney > 0 ? String(s.remainingMoney) : "",
            });
          })
          .catch(() => setDiscountSummary(null));
      }
    } else {
      setItems([{ description: "", quantity: 1, unitPrice: 0, discount: 0, manual: true }]);
      setDiscountSummary(null);
      setDiscountApply({ days: "", amount: "" });
      setLoadingItems(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Enable Timbrar once the user reaches the bottom of the modal. The sentinel
  // only exists after the content finishes loading; if the content is short
  // enough to need no scrolling, it's visible immediately and the observer
  // fires right away.
  useEffect(() => {
    if (!open || loadingItems || success) return;
    const node = bottomSentinelRef.current;
    if (!node) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setScrolledToBottom(true);
      },
      { threshold: 1.0 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [open, loadingItems, success, items.length]);

  const updateItem = (index, field, value) => {
    const updated = [...items];
    const target = updated[index];
    let v = value;
    // Cap a rental line's day count to the billing period span. Billing FEWER
    // days than the period is allowed (comped/free days); billing more is not.
    // Matches the day math the period-change handlers use, so a one-week period
    // caps Cantidad at 7. Non-rental (fee/manual) lines are never capped.
    if (field === "quantity" && target?.isRental) {
      const cap = inclusiveDays(periodFrom, periodTo);
      const n = Math.floor(Number(value));
      if (cap != null && isFinite(n) && n > cap) v = String(cap);
    }
    const next = { ...target, [field]: v };
    // Editing an amount-affecting field overrides the agreed-amount target;
    // drop intendedTotal so the line bills exactly what the vendor typed.
    if (field === "quantity" || field === "unitPrice" || field === "discount") {
      delete next.intendedTotal;
    }
    updated[index] = next;
    setItems(updated);
  };

  // Day span of the selected billing period — the max any rental line may bill.
  // You can bill FEWER days (comped/free days) but never more than the period.
  const rentDayCap = inclusiveDays(periodFrom, periodTo);

  // Date-picker bounds for the billing period:
  //  · start  = reservation start day
  //  · end    = reservation's LAST BILLABLE day (first_day + rounded_days − 1),
  //    since periodTo is inclusive; the raw return date (upper of date_range)
  //    would allow one extra day beyond the rental's billable span.
  //  · earliest selectable = the day AFTER the last already-invoiced period, so
  //    already-billed days can't be picked again (skipped when substituting,
  //    which intentionally re-bills the original period).
  const resRange = reservation?.date_range
    ? String(reservation.date_range).replace(/[[\]()]/g, "").split(",")
    : null;
  const resStartDay = resRange
    ? resRange[0]?.replace(/"/g, "").trim().substring(0, 10) || null
    : null;
  const resEndDay = reservation?.date_range
    ? reservationLastBillableDay(reservation.date_range)
    : null;
  const lastInvoicedTo =
    !isSubstitution && invoicedPeriods.length > 0
      ? [...invoicedPeriods].sort(
          (a, b) => new Date(b.period_to) - new Date(a.period_to)
        )[0].period_to
      : null;
  const periodMinDate = lastInvoicedTo
    ? addDaysISO(lastInvoicedTo, 1)
    : resStartDay;

  const addItem = () => {
    setItems([...items, { description: "", quantity: 1, unitPrice: 0, manual: true }]);
  };

  const removeItem = (index) => {
    if (items.length <= 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  // Manual concept: bind the line to an existing Alegra product. Its list price
  // (pre-IVA in Alegra) pre-fills the IVA-inclusive price if none was typed.
  const pickProduct = (index, product) => {
    const listPrice = Number(product?.price?.[0]?.price) || 0;
    setItems((prev) =>
      prev.map((it, i) =>
        i === index
          ? {
              ...it,
              alegraItemId: Number(product.id),
              description: product.name,
              preResolvedUnit: product.inventory?.unit || product.unit || null,
              manual: true,
              unitPrice:
                Number(it.unitPrice) > 0
                  ? it.unitPrice
                  : Math.round(listPrice * 1.16 * 100) / 100,
            }
          : it
      )
    );
  };

  // Apply the user's manual discount. `items` stays the full-price base (so
  // grossTotal / "Descuento" reflect the discount); effectiveItems is what
  // actually gets billed and saved. Every line is passed in — days only drop
  // rent, but the peso amount spills over to fees/cargos when rent can't
  // absorb it (e.g. a charges-only top-up invoice).
  const discDays = Math.max(0, parseInt(discountApply.days, 10) || 0);
  const discAmount = Math.max(0, parseFloat(discountApply.amount) || 0);
  const rentBase = items.filter((it) => it.isRental);
  const applied = applyInvoiceDiscount(items, { days: discDays, amount: discAmount });
  const effByKey = {};
  for (const l of applied.lines) effByKey[l.key] = l;
  // Rent lines fully consumed by the day-drop disappear; fee lines are kept.
  const effectiveItems = applied.lines;
  const rentRate = rentBase[0]?.unitPrice || discountSummary?.dailyPrice || 0;
  // Guard: can't draw more than the reservation's recorded discount.
  const discountOver =
    !!discountSummary &&
    (discDays > discountSummary.remainingDays ||
      discAmount > discountSummary.remainingMoney + 0.005);

  // Prices in the system already include IVA — calculate backwards.
  // grossTotal is the FULL (pre-discount) rent + fees, so the "Descuento" line
  // shows the whole manual discount (dropped days + peso amount). `total` is the
  // TRUE target the CFDI stamps at, from the effective (discounted) lines.
  const grossTotal = items.reduce(
    (sum, item) => sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0),
    0
  );
  const total = Math.round(
    effectiveItems.reduce((sum, item) => {
      if (item.intendedTotal != null) return sum + item.intendedTotal;
      const q = parseFloat(item.quantity) || 0;
      const up = parseFloat(item.unitPrice) || 0;
      const d = parseFloat(item.discount) || 0;
      return sum + (up - d) * q;
    }, 0) * 100
  ) / 100;
  // Discount shown = list price − final, so the breakdown always ties to `total`.
  const totalDiscount = Math.max(0, Math.round((grossTotal - total) * 100) / 100);
  const subtotal = total / 1.16;
  const tax = total - subtotal;

  // Auto PUE/PPD: if the reservation already has enough payment to fully cover
  // this invoice → PUE (paid up front), else PPD. Skips once the user overrides.
  // (Declared after `total` so the dependency array doesn't hit its TDZ.)
  useEffect(() => {
    if (!open || paymentTypeTouched || !(total > 0)) return;
    const covered = availablePayment + 0.01 >= total;
    setBilling((prev) => ({
      ...prev,
      paymentType: covered ? "PUE" : "PPD",
      paymentForm: covered ? coverForm || prev.paymentForm : "other",
      creditDays: covered ? "" : prev.creditDays,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, availablePayment, coverForm, total, paymentTypeTouched]);

  // `publicoGeneral` comes from the "Timbrar General" button: the CFDI goes to
  // the generic RFC (contact 1, S01, régimen 616, PUE) even though the client
  // has fiscal data on file. It's a per-invoice override — the form state and
  // the client record are left untouched.
  const submit = async (stamp, { publicoGeneral = false } = {}) => {
    setSaving(true);
    setError(null);

    try {
      const effBilling = publicoGeneral ? publicoGeneralBilling(billing) : billing;

      // 0. Substitution preflight. Everything that can make the cancel of the
      //    ORIGINAL fail has to be resolved BEFORE we stamp the replacement —
      //    a stamped CFDI is irreversible, so failing after that point leaves a
      //    duplicate live at SAT and a retry would mint yet another one.
      if (isSubstitution) {
        if (substitutesInvoice.status === "void") {
          throw new Error(
            `${substitutesInvoice.invoice_number} ya está cancelada. ` +
              `Emite una factura normal en lugar de una sustitución.`
          );
        }
        // The DB can lag Alegra: a cancel requested from Alegra's web UI never
        // reaches invoices_issued, and Alegra then refuses our motivo-01 void
        // AFTER the substitute is already stamped (GOMV4814 → GOMV4836).
        const alegraCancel = await getAlegraCancelState(substitutesInvoice.alegra_id);
        if (alegraCancel) {
          throw new Error(
            `${substitutesInvoice.invoice_number} ya está ${alegraCancel} en Alegra, ` +
              `así que no se puede sustituir. Emite una factura normal y avisa a ` +
              `administración para registrar la cancelación en el sistema.`
          );
        }
        // Alegra rejects the void while payments are applied. Detaching here
        // (not after stamping) is what makes a failure recoverable.
        await detachInvoicePayments(substitutesInvoice.alegra_id);
      }

      // 1. Find or create contact in Alegra
      const contactId = await findOrCreateContact(effBilling);

      // 2. Save billing settings on the customer/company for next time. Skipped
      //    for a Público en General override: it would overwrite the client's
      //    real régimen / uso de CFDI and point alegra_contact_id at contact 1.
      if (!publicoGeneral) {
        await saveBillingSettings(effBilling, {
          customerId: customer?.id,
          companyId: company?.id,
          alegraContactId: contactId,
        });
      }

      // 3. Build the Alegra line items from the EFFECTIVE (post-discount) lines
      //    (lookup/create products, set units, compute 6-decimal priceBeforeTax,
      //    apply line descriptions). Zero-day rent lines are already dropped.
      const alegraItems = await buildAlegraItems(
        effectiveItems.filter((it) => (parseFloat(it.quantity) || 0) > 0),
        {
          periodFrom,
          periodTo,
          invoiceNotes,
        }
      );

      // 4. Apply the rounding adjustment so our DB total matches Alegra's.
      applyRoundingAdjustment(alegraItems, total);

      // 5. Create invoice in Alegra.
      const { payload: invoicePayload, today } = buildInvoicePayload({
        billing: effBilling,
        alegraItems,
        contactId,
        stamp,
      });
      const invoice = await alegraRequest("invoices", "POST", invoicePayload);

      // 5. Save to our DB
      const newNumber = invoice.numberTemplate?.fullNumber || String(invoice.id);
      const { data: savedInvoice, error: insErr } = await supabase
        .from("invoices_issued")
        .insert({
          alegra_id: String(invoice.id),
          cfdi_uuid: cfdiUuidOf(invoice),
          reservation_id: reservation?.id || null,
          customer_id: customer?.id || null,
          company_id: company?.id || null,
          alegra_contact_id: String(contactId),
          invoice_number: newNumber,
          date: today,
          subtotal: subtotal,
          tax: tax,
          total: total,
          status: invoice.status || (stamp ? "open" : "draft"),
          stamped: stamp,
          cfdi_use: effBilling.cfdiUse,
          payment_method: effBilling.paymentForm,
          payment_type: effBilling.paymentType,
          created_by: session?.user?.id,
          period_from: periodFrom || null,
          period_to: periodTo || null,
          discount_applied: applied.discountApplied || 0,
          discount_days: applied.discountDays || 0,
        })
        .select("id")
        .single();
      if (insErr) throw new Error(insErr.message);

      // 5a. Link the extra charges this CFDI actually billed, so the next
      //     invoice for this reservation doesn't propose them again. Driven by
      //     the effective lines, so a charge the user deleted or discounted
      //     away stays billable.
      await markChargesBilled(
        effectiveItems
          .filter((it) => it.chargeId && (parseFloat(it.quantity) || 0) > 0)
          .map((it) => it.chargeId),
        savedInvoice.id
      );

      // 5b. Substitution: cancel the original citing the new folio (motivo 01),
      //     then link both. Its payments were already detached in step 0, so the
      //     void should go through; substitute_invoice frees the allocations back
      //     to their transactions as reservation credit, and the block below
      //     re-applies that credit to the new invoice (DB + Alegra).
      //     The new CFDI is already stamped/saved, so a failure here does NOT
      //     roll it back — the message tells you to finish the cancel by hand
      //     rather than re-running this flow (which would stamp a duplicate).
      if (isSubstitution) {
        try {
          await alegraRequest(`invoices/${substitutesInvoice.alegra_id}/void`, "POST", {
            cause: "01",
            relatedFolio: newNumber,
          });
          const { error: subErr } = await supabase.rpc("substitute_invoice", {
            p_original_id: substitutesInvoice.id,
            p_new_id: savedInvoice.id,
            p_motivo: "01",
            p_reason: null,
            p_user: session?.user?.id || null,
          });
          if (subErr) throw new Error(subErr.message);
        } catch (e) {
          throw new Error(
            `${newNumber} se timbró correctamente, pero no se pudo cancelar ` +
              `${substitutesInvoice.invoice_number}: ${e.message}. ` +
              `NO vuelvas a re-facturar (timbrarías otro duplicado): cancela ` +
              `${substitutesInvoice.invoice_number} desde sus acciones, motivo 01, ` +
              `relacionando el folio ${newNumber}.`
          );
        }
      }

      // Pay-first → PUE: link the reservation's existing payment(s) to this
      // invoice so it's marked paid. PUE ⇒ no complemento; applyAllocations
      // registers the payment in Alegra. Also covers substitutions: the credit
      // freed from the cancelled original lands back on the new invoice, which
      // re-creates the Alegra payment that step 0 had to delete.
      // Non-fatal: the invoice is already stamped/saved, so a failure just
      // leaves it to manual conciliation.
      if (stamp && effBilling.paymentType === "PUE" && reservation?.id) {
        try {
          await allocateAvailableToInvoice({
            invoice: {
              id: savedInvoice.id,
              alegra_id: String(invoice.id),
              payment_type: "PUE",
              total,
              amount_paid: 0,
              customer_id: customer?.id || null,
              company_id: company?.id || null,
              alegra_contact_id: String(contactId),
              status: invoice.status,
            },
            reservationIds: reservation.id,
            createdBy: session?.user?.id,
          });
        } catch (e) {
          console.error("PUE auto-allocation failed:", e);
        }
      }

      // Fetch PDF/XML links
      let pdfUrl = null, xmlUrl = null;
      try {
        const withFiles = await alegraRequest(`invoices/${invoice.id}?fields=pdf,xml`, "GET");
        pdfUrl = withFiles?.pdf || null;
        xmlUrl = withFiles?.xml || null;
      } catch {}

      setSuccess({
        id: invoice.id,
        number: invoice.numberTemplate?.fullNumber || invoice.id,
        stamped: stamp,
        pdfUrl,
        xmlUrl,
      });

      if (onSuccess) onSuccess();
    } catch (err) {
      // If stamping failed, Alegra may have created a draft — delete it
      const draftId = err.alegraResponse?.invoice?.id;
      if (draftId) {
        try {
          await alegraRequest(`invoices/${draftId}`, "DELETE");
        } catch {}
      }
      setError(err.message || "Error al crear factura");
    } finally {
      setSaving(false);
    }
  };

  const sendInvoiceEmail = async () => {
    if (!emailTo || !success) return;
    setEmailSending(true);
    setEmailError(null);

    try {
      await alegraRequest(`invoices/${success.id}/email`, "POST", {
        emails: [emailTo],
        sendCopyToUser: false,
      });

      // Save email to customer/company if changed
      const entity = company || customer;
      const currentEmail = entity?.email || entity?.contact_email || "";
      if (emailTo !== currentEmail && entity) {
        if (company) {
          await supabase.from("companies").update({ contact_email: emailTo }).eq("id", company.id);
        } else if (customer) {
          await supabase.from("customers").update({ email: emailTo }).eq("id", customer.id);
        }
      }

      setEmailSent(true);
    } catch (err) {
      setEmailError(err.message || "Error al enviar");
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isSubstitution ? "Corregir y re-facturar" : "Facturar"}
      primaryAction={
        success
          ? { content: "Cerrar", onAction: onClose }
          : {
              content: saving
                ? "Procesando..."
                : isSubstitution
                ? "Timbrar y sustituir"
                : "Timbrar",
              onAction: () => submit(true),
              loading: saving,
              disabled: saving || loadingItems || !scrolledToBottom || items.length === 0 || total <= 0 || !!periodError || discountOver,
            }
      }
      secondaryActions={success ? [] : []}
      // Sits to the left of Timbrar. Same invoice, stamped to the generic RFC
      // instead of the client's — armed only after a 4s hover so it can't be
      // hit by accident.
      footer={
        success ? null : (
          <HoverArmButton
            onAction={() => submit(true, { publicoGeneral: true })}
            loading={saving}
            disabled={
              saving ||
              loadingItems ||
              !scrolledToBottom ||
              items.length === 0 ||
              total <= 0 ||
              !!periodError ||
              discountOver
            }
          >
            Timbrar General
          </HoverArmButton>
        )
      }
      large
    >
      <Modal.Section>
        {!success && isSubstitution && (
          <div style={{ marginBottom: "1rem" }}>
            <Banner tone="warning">
              Sustituye a {substitutesInvoice.invoice_number}. Al timbrar, esta
              factura nueva reemplaza a la anterior y la cancela (motivo 01); los
              pagos se mueven a la nueva.
            </Banner>
          </div>
        )}
        {success ? (
          <BlockStack gap="400">
            <Banner tone="success">
              <p>
                Factura {success.number} creada exitosamente
                {success.stamped ? " y timbrada" : " como borrador"}.
              </p>
            </Banner>
            <InlineGrid columns={2} gap="200">
              {success.pdfUrl && (
                <Button url={success.pdfUrl} external>
                  Descargar PDF
                </Button>
              )}
              {success.xmlUrl && (
                <Button url={success.xmlUrl} external>
                  Descargar XML
                </Button>
              )}
            </InlineGrid>

            <Divider />

            {emailSent ? (
              <Banner tone="success">
                <p>Factura enviada a {emailTo}</p>
              </Banner>
            ) : (
              <BlockStack gap="200">
                <Text variant="headingSm">Enviar por correo</Text>
                {emailError && (
                  <Banner tone="critical">
                    <p>{emailError}</p>
                  </Banner>
                )}
                <InlineGrid columns="1fr auto" gap="200">
                  <TextField
                    label="Correo"
                    value={emailTo}
                    onChange={setEmailTo}
                    type="email"
                    placeholder="cliente@ejemplo.com"
                    labelHidden
                  />
                  <div style={{ paddingTop: "0.25rem" }}>
                    <Button
                      variant="primary"
                      onClick={sendInvoiceEmail}
                      loading={emailSending}
                      disabled={!emailTo || emailSending}
                    >
                      Enviar
                    </Button>
                  </div>
                </InlineGrid>
              </BlockStack>
            )}
          </BlockStack>
        ) : (
          <BlockStack gap="400">
            {error && (
              <Banner tone="critical">
                <p>{error}</p>
              </Banner>
            )}

            {/* Billing Data */}
            <Text variant="headingSm">Datos de Facturación</Text>
            <InlineGrid columns={2} gap="200">
              <TextField
                label="RFC"
                value={billing.rfc}
                onChange={(val) =>
                  setBilling({ ...billing, rfc: val.toUpperCase() })
                }
                requiredIndicator
                disabled
              />
              <TextField
                label="Razón Social"
                value={billing.razonSocial}
                onChange={(val) =>
                  setBilling({ ...billing, razonSocial: val })
                }
                disabled
              />
            </InlineGrid>
            <InlineGrid columns={3} gap="200">
              <Select
                label="Régimen Fiscal"
                value={billing.regimenFiscal}
                options={REGIMEN_OPTIONS}
                onChange={(val) =>
                  setBilling({ ...billing, regimenFiscal: val })
                }
                disabled
              />
              <TextField
                label="Código Postal"
                value={billing.codigoPostal}
                onChange={(val) =>
                  setBilling({ ...billing, codigoPostal: val.replace(/\D/g, "").substring(0, 5) })
                }
                maxLength={5}
                disabled
              />
              <Select
                label="Uso de CFDI"
                value={billing.cfdiUse}
                options={CFDI_USE_OPTIONS}
                onChange={(val) =>
                  setBilling({ ...billing, cfdiUse: val })
                }
                disabled={billing.rfc === "XAXX010101000"}
              />
            </InlineGrid>
            <InlineGrid columns={billing.paymentType === "PPD" ? 3 : 2} gap="200">
              <Select
                label="Método de Pago"
                value={billing.paymentType}
                options={PAYMENT_TYPE_OPTIONS}
                helpText={
                  reservation
                    ? `Pago disponible: ${formatPrice(availablePayment)} · auto: ${availablePayment + 0.01 >= total && total > 0 ? "PUE" : "PPD"}`
                    : undefined
                }
                onChange={(val) => {
                  setPaymentTypeTouched(true);
                  setBilling({
                    ...billing,
                    paymentType: val,
                    // SAT rule: PPD invoices must use Forma de Pago
                    // "99 — Por definir". Lock it when switching to PPD.
                    paymentForm:
                      val === "PPD" ? "other" : billing.paymentForm,
                  });
                }}
              />
              {billing.paymentType === "PPD" && (
                <TextField
                  label="Días de crédito"
                  type="number"
                  min={0}
                  suffix="días"
                  value={
                    billing.creditDays === "" || billing.creditDays == null
                      ? ""
                      : String(billing.creditDays)
                  }
                  onChange={(val) =>
                    setBilling({ ...billing, creditDays: val.replace(/\D/g, "") })
                  }
                  autoComplete="off"
                  helpText={`Vence: ${addDaysISO(
                    new Date().toLocaleDateString("en-CA"),
                    parseInt(billing.creditDays, 10) || 0
                  )}`}
                />
              )}
              <Select
                label="Forma de Pago"
                value={billing.paymentForm}
                options={
                  billing.paymentType === "PPD"
                    ? PAYMENT_FORM_OPTIONS.filter(
                        (o) => o.value === "other"
                      )
                    : PAYMENT_FORM_OPTIONS
                }
                onChange={(val) =>
                  setBilling({ ...billing, paymentForm: val })
                }
                disabled={billing.paymentType === "PPD"}
                helpText={
                  billing.paymentType === "PPD"
                    ? "PPD sólo admite 99 — Por definir."
                    : undefined
                }
              />
            </InlineGrid>

            <Divider />

            {loadingItems ? (
              <BlockStack inlineAlign="center" gap="200">
                <div style={{ padding: "2rem 0" }}>
                  <Spinner accessibilityLabel="Calculando conceptos" size="large" />
                </div>
                <Text tone="subdued">Calculando periodo y conceptos…</Text>
              </BlockStack>
            ) : (
            <>
            {/* Billing Period */}
            <Text variant="headingSm">Periodo de Facturación</Text>
            <InlineGrid columns={2} gap="200">
              <TextField
                label="Desde"
                type="date"
                value={periodFrom}
                onChange={handlePeriodFromChange}
                min={periodMinDate || undefined}
                max={resEndDay || undefined}
              />
              <TextField
                label="Hasta"
                type="date"
                // Shown as the CHECKOUT day (the return date), which is NOT
                // billed — so "26 → 03" reads as 7 days (rental-night
                // convention). Internally periodTo stays the last BILLED day
                // (inclusive) to match buildRentLines and stored periods, hence
                // the +1 on display and −1 on change.
                value={periodTo ? addDaysISO(periodTo, 1) : ""}
                onChange={(v) => handlePeriodToChange(v ? addDaysISO(v, -1) : "")}
                min={periodFrom ? addDaysISO(periodFrom, 1) : undefined}
                max={resEndDay ? addDaysISO(resEndDay, 1) : undefined}
              />
            </InlineGrid>
            {reservation && totalInvoiced > 0 && (
              <Banner tone={totalInvoiced >= reservation.total_price ? "critical" : "info"}>
                <p>
                  Facturado: ${totalInvoiced.toLocaleString()} de ${reservation.total_price?.toLocaleString()}
                  {totalInvoiced >= reservation.total_price && " — Esta reserva ya está completamente facturada."}
                </p>
              </Banner>
            )}
            {periodError && (
              <Banner tone="warning">
                <p>{periodError}</p>
              </Banner>
            )}
            {invoicedPeriods.length > 0 && (() => {
              const sorted = [...invoicedPeriods].sort(
                (a, b) => new Date(a.period_from) - new Date(b.period_from)
              );
              const latest = sorted[sorted.length - 1];
              const earlier = sorted.slice(0, -1);
              // period_to is the last billed day; show checkout (+1) to match
              // the Hasta field's convention.
              const line = (p) =>
                `${p.invoice_number || `#${p.id}`}: ${p.period_from} → ${addDaysISO(p.period_to, 1)}`;
              return (
                <BlockStack gap="100">
                  <Text variant="bodySm" tone="subdued">Periodos ya facturados:</Text>
                  {showAllPeriods &&
                    earlier.map((p) => (
                      <Text key={p.id} variant="bodySm">{line(p)}</Text>
                    ))}
                  <Text variant="bodySm">{line(latest)}</Text>
                  {earlier.length > 0 && (
                    <Button
                      variant="plain"
                      onClick={() => setShowAllPeriods((v) => !v)}
                    >
                      {showAllPeriods
                        ? "Ocultar anteriores"
                        : `Ver ${earlier.length} anteriores`}
                    </Button>
                  )}
                </BlockStack>
              );
            })()}

            {/* Draw down the reservation's RECORDED discount (days or money) —
                no ad-hoc discounts. Only shown when something is available. */}
            {(() => {
              const sum = discountSummary;
              if (!sum || (sum.remainingDays <= 0 && sum.remainingMoney <= 0)) return null;
              const hasRent = rentBase.length > 0;
              const overDays = discDays > sum.remainingDays;
              const overMoney = discAmount > sum.remainingMoney + 0.005;
              const nInputs =
                (sum.remainingDays > 0 ? 1 : 0) + (sum.remainingMoney > 0 ? 1 : 0);
              const appliedPesos = discDays * rentRate + applied.discountApplied;
              return (
                <Box background="bg-surface-secondary" padding="300" borderRadius="200">
                  <BlockStack gap="200">
                    <Text variant="headingSm">Descuento</Text>
                    <Text variant="bodySm" tone="subdued">
                      Disponible de la reserva:{" "}
                      {sum.remainingDays > 0
                        ? `${sum.remainingDays} día${sum.remainingDays === 1 ? "" : "s"}`
                        : ""}
                      {sum.remainingDays > 0 && sum.remainingMoney > 0 ? " · " : ""}
                      {sum.remainingMoney > 0 ? formatPrice(sum.remainingMoney) : ""}
                    </Text>
                    <InlineGrid columns={nInputs === 2 ? "1fr 1fr" : "1fr"} gap="200">
                      {sum.remainingDays > 0 && (
                        <TextField
                          label="Días a descontar"
                          type="number"
                          min={0}
                          max={sum.remainingDays}
                          value={String(discountApply.days ?? "")}
                          onChange={(v) => setDiscountApply((p) => ({ ...p, days: v }))}
                          suffix={hasRent ? `× ${formatPrice(rentRate)}` : undefined}
                          disabled={!hasRent}
                          error={overDays ? `Máx ${sum.remainingDays}` : undefined}
                          autoComplete="off"
                        />
                      )}
                      {sum.remainingMoney > 0 && (
                        <TextField
                          label="Monto a descontar"
                          type="number"
                          min={0}
                          prefix="$"
                          value={String(discountApply.amount ?? "")}
                          onChange={(v) => setDiscountApply((p) => ({ ...p, amount: v }))}
                          error={overMoney ? `Máx ${formatPrice(sum.remainingMoney)}` : undefined}
                          autoComplete="off"
                        />
                      )}
                    </InlineGrid>
                    {appliedPesos > 0 && (
                      <Text variant="bodySm" tone="success">
                        Descuento aplicado en esta factura: {formatPrice(appliedPesos)}
                        {applied.discountDays > 0
                          ? ` (${applied.discountDays} día${applied.discountDays === 1 ? "" : "s"})`
                          : ""}
                      </Text>
                    )}
                  </BlockStack>
                </Box>
              );
            })()}

            <TextField
              label="Notas (opcional)"
              value={invoiceNotes}
              onChange={setInvoiceNotes}
              placeholder="Texto adicional para la descripción en la factura"
              multiline={2}
            />

            <Divider />

            {/* Line Items */}
            <InlineGrid columns="1fr auto">
              <Text variant="headingSm">Conceptos</Text>
              <Button icon={PlusIcon} onClick={addItem} size="slim">
                Agregar
              </Button>
            </InlineGrid>

            {items.map((item, i) => (
              <InlineGrid key={i} columns="3fr 1fr 1fr auto" gap="200">
                {item.manual && !item.alegraItemId ? (
                  <AlegraProductPicker
                    label={i === 0 ? "Descripción" : ""}
                    onSelect={(product) => pickProduct(i, product)}
                  />
                ) : (
                  <BlockStack gap="100">
                    <TextField
                      label={i === 0 ? "Descripción" : ""}
                      value={item.description}
                      placeholder="Concepto"
                      disabled
                      autoComplete="off"
                    />
                    {item.manual && (
                      <TextField
                        label="Nota del concepto"
                        labelHidden
                        value={item.linePrefix || ""}
                        onChange={(val) => updateItem(i, "linePrefix", val)}
                        placeholder="Nota (opcional)"
                        autoComplete="off"
                      />
                    )}
                  </BlockStack>
                )}
                <TextField
                  label={i === 0 ? "Cantidad" : ""}
                  // Rent quantity is governed by the discount control below
                  // (days dropped reduce it), so it's read-only here and shows
                  // the effective billed days. Fee lines stay editable.
                  value={String(
                    item.isRental
                      ? effByKey[item.key]?.quantity ?? 0
                      : item.quantity
                  )}
                  onChange={(val) => updateItem(i, "quantity", val)}
                  type="number"
                  disabled={!!item.isRental}
                  min={item.isRental ? 0 : undefined}
                  suffix={item.isRental ? "días" : undefined}
                />
                <TextField
                  label={i === 0 ? "Precio Lista" : ""}
                  value={String(item.unitPrice)}
                  onChange={(val) => updateItem(i, "unitPrice", val)}
                  type="number"
                  prefix="$"
                />
                <div style={{ paddingTop: i === 0 ? "1.5rem" : 0 }}>
                  <Button
                    icon={DeleteIcon}
                    variant="plain"
                    tone="critical"
                    onClick={() => removeItem(i)}
                    disabled={items.length <= 1}
                  />
                </div>
              </InlineGrid>
            ))}

            <Divider />

            {/* Totals */}
            <BlockStack gap="100">
              <InlineGrid columns="1fr auto">
                <Text>Precio Lista</Text>
                <Text>{formatPrice(grossTotal / 1.16)}</Text>
              </InlineGrid>
              {totalDiscount > 0 && (
                <InlineGrid columns="1fr auto">
                  <Text tone="success">Descuento</Text>
                  <Text tone="success">-{formatPrice(totalDiscount / 1.16)}</Text>
                </InlineGrid>
              )}
              <InlineGrid columns="1fr auto">
                <Text>Subtotal</Text>
                <Text>{formatPrice(subtotal)}</Text>
              </InlineGrid>
              <InlineGrid columns="1fr auto">
                <Text>IVA (16%)</Text>
                <Text>{formatPrice(tax)}</Text>
              </InlineGrid>
              <InlineGrid columns="1fr auto">
                <Text variant="headingSm" fontWeight="bold">Total</Text>
                <Text variant="headingSm" fontWeight="bold">
                  {formatPrice(total)}
                </Text>
              </InlineGrid>
            </BlockStack>
            {/* Sentinel: Timbrar unlocks once this reaches view (bottom). */}
            <div ref={bottomSentinelRef} style={{ height: 1 }} />
            </>
            )}
          </BlockStack>
        )}
      </Modal.Section>
    </Modal>
  );
}

export default InvoiceModal;
